/**
 * Customer PWA error-boundary Sentry tags (Engineering §10 observability).
 */
import type { ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { captureBoundaryError } from 'pi-kiosk-shared/sentry';

export interface CustomerBoundaryContext {
  readonly salesPointCapabilityVersion?: string;
  readonly authProvider?: string;
  readonly activeFlagsSnapshot?: string;
  /** L1 root only — nested RemountBoundary must omit these. */
  readonly boundary_layer?: string;
  readonly msId?: string;
  /** Local Sentry/logger correlation only — never handed to RecoveryHost. */
  readonly correlation_id?: string;
  readonly feature?: string;
}

/** Optional L1/L2/L3 boundary tags — unset for nested RemountBoundary defaults. */
export interface CustomerBoundaryCaptureOptions {
  readonly boundary_layer?: string;
  readonly msId?: string;
  readonly feature?: string;
  readonly correlation_id?: string;
}

export function resolveSalesPointCapabilityVersionTag(
  version: string | null | undefined,
): string {
  if (version === null || version === undefined || version.trim().length === 0) {
    return 'unknown';
  }
  return version;
}

export function resolveAuthProviderTag(provider: string | null | undefined): string | undefined {
  if (provider === null || provider === undefined || provider.trim().length === 0) {
    return undefined;
  }
  return provider;
}

function resolveFlowState(pathname: string): string {
  if (pathname.includes('/shop/scanner') || pathname === '/scan' || pathname.startsWith('/scan/')) {
    return 'scanner';
  }
  if (pathname.includes('/shop')) {
    return 'shop';
  }
  if (pathname.includes('/checkout')) {
    return 'checkout';
  }
  if (pathname.includes('/sign-in') || pathname.includes('/sign-up')) {
    return 'auth';
  }
  if (pathname.includes('/account')) {
    return 'account';
  }
  return 'retail_other';
}

function resolveReceiptGuardState(pathname: string, search: string): string | undefined {
  if (!pathname.includes('/checkout/complete')) {
    return undefined;
  }
  return search.includes('view=receipt') ? 'receipt_panel_open' : 'success_screen';
}

function resolveScannerDegradedMode(pathname: string): string | undefined {
  if (pathname === '/scan' || pathname.startsWith('/scan/')) {
    return 'manual_route';
  }
  if (!pathname.includes('/shop')) {
    return undefined;
  }
  return pathname.includes('/shop/scanner') ? 'manual_route' : 'shop_catalog';
}

/** Emit boundary error with retail flow context tags. */
export function captureCustomerBoundaryError(
  error: Error,
  errorInfo: ErrorInfo,
  context?: CustomerBoundaryContext,
  options?: CustomerBoundaryCaptureOptions,
): void {
  if (typeof window !== 'undefined') {
    const { pathname, search } = window.location;
    Sentry.withScope((scope) => {
      scope.setTag('flow_state', resolveFlowState(pathname));
      scope.setTag('app', 'customer');
      scope.setTag(
        'sales_point_capability_version',
        resolveSalesPointCapabilityVersionTag(context?.salesPointCapabilityVersion),
      );
      if (context?.activeFlagsSnapshot !== undefined && context.activeFlagsSnapshot.length > 0) {
        scope.setTag('active_flags_snapshot', context.activeFlagsSnapshot);
      }
      const authProvider = resolveAuthProviderTag(context?.authProvider);
      if (authProvider !== undefined) {
        scope.setTag('auth_provider', authProvider);
      }
      const receiptGuard = resolveReceiptGuardState(pathname, search);
      if (receiptGuard !== undefined) {
        scope.setTag('receipt_guard_state', receiptGuard);
      }
      const scannerMode = resolveScannerDegradedMode(pathname);
      if (scannerMode !== undefined) {
        scope.setTag('scanner_degraded_mode', scannerMode);
      }
      if (options?.boundary_layer !== undefined) {
        scope.setTag('boundary_layer', options.boundary_layer);
      }
      if (options?.msId !== undefined) {
        scope.setTag('msId', options.msId);
      }
      if (options?.feature !== undefined) {
        scope.setTag('feature', options.feature);
      }
      if (options?.correlation_id !== undefined) {
        scope.setTag('correlation_id', options.correlation_id);
      }
      captureBoundaryError(error, errorInfo);
    });
    return;
  }
  captureBoundaryError(error, errorInfo);
}
